// ── Wallet core: mnemonic, per-chain key derivation ───────────────────────────
// Simplified copy for the dashboard: only derivation, no vault encryption.
import * as bip39 from 'bip39';
import { HDNodeWallet, Mnemonic, keccak256, getBytes } from 'ethers';
import { BIP32Factory } from 'bip32';
import * as ecc from '@bitcoinerlab/secp256k1';
import * as bitcoin from 'bitcoinjs-lib';
import { derivePath as edDerivePath } from 'ed25519-hd-key';
import { Keypair } from '@solana/web3.js';
import bs58 from 'bs58';
import { sha256 } from '@noble/hashes/sha256';

const bip32 = BIP32Factory(ecc);
bitcoin.initEccLib(ecc);

export function normalizePhrase(phrase) {
  return (phrase || '').trim().split(/\s+/).join(' ').toLowerCase();
}

function base58checkEncode(payload) {
  const checksum = sha256(sha256(payload)).slice(0, 4);
  const full = new Uint8Array(payload.length + 4);
  full.set(payload); full.set(checksum, payload.length);
  return bs58.encode(full);
}

function deriveEvm(mnemonic, index) {
  const w = HDNodeWallet.fromMnemonic(Mnemonic.fromPhrase(mnemonic), `m/44'/60'/0'/0/${index}`);
  return { address: w.address, privateKey: w.privateKey };
}

function deriveBitcoin(mnemonic, index) {
  const seed = bip39.mnemonicToSeedSync(mnemonic);
  const root = bip32.fromSeed(seed);
  const child = root.derivePath(`m/84'/0'/0'/0/${index}`);
  const { address } = bitcoin.payments.p2wpkh({ pubkey: Buffer.from(child.publicKey), network: bitcoin.networks.bitcoin });
  return { address, privateKeyWIF: child.toWIF(), publicKey: Buffer.from(child.publicKey).toString('hex') };
}

function deriveSolana(mnemonic, index) {
  const seed = bip39.mnemonicToSeedSync(mnemonic);
  const { key } = edDerivePath(`m/44'/501'/${index}'/0'`, seed.toString('hex'));
  const kp = Keypair.fromSeed(Uint8Array.from(key.slice(0, 32)));
  return { address: kp.publicKey.toBase58(), secretKey: bs58.encode(kp.secretKey) };
}

function deriveTron(mnemonic, index) {
  const seed = bip39.mnemonicToSeedSync(mnemonic);
  const root = bip32.fromSeed(seed);
  const child = root.derivePath(`m/44'/195'/0'/0/${index}`);
  const pub = ecc.pointFromScalar(child.privateKey, false);
  const hash = keccak256(getBytes(pub.slice(1)));
  const addressBytes = new Uint8Array(21);
  addressBytes[0] = 0x41;
  addressBytes.set(getBytes(hash).slice(-20), 1);
  return { address: base58checkEncode(addressBytes), privateKey: Buffer.from(child.privateKey).toString('hex') };
}

export function deriveAccount(mnemonic, index = 0) {
  const evm = deriveEvm(mnemonic, index);
  return {
    index,
    evm,
    bitcoin: deriveBitcoin(mnemonic, index),
    solana: deriveSolana(mnemonic, index),
    tron: deriveTron(mnemonic, index),
  };
}
